import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Input, Modal, Space, Spin, Steps, Typography, message } from 'antd'
import { CopyOutlined, KeyOutlined, ReloadOutlined } from '@ant-design/icons'
import { withRouter } from 'react-router-dom'
import { generateVibeDbPrompt } from '../../request'
import {
    getVibeCodexGuideSceneMessage,
    normalizeVibeCodexGuideResponse,
    VIBE_CODEX_GUIDE_SCENE,
} from './vibeCodexGuide'

const { Paragraph, Text } = Typography

const EMPTY_GUIDE = {
    prompt: '',
    promptVersion: '',
    scopeType: '',
    authorizedProjectCount: 0,
    missingProjectNames: [],
    customPromptApplied: false,
}

const VibeCodexGuideDialog = ({
    visible,
    accountId,
    scene = VIBE_CODEX_GUIDE_SCENE.NORMAL,
    onClose,
    history,
}) => {
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [guide, setGuide] = useState(EMPTY_GUIDE)
    const requestSequence = useRef(0)

    const loadGuide = useCallback(() => {
        const sequence = requestSequence.current + 1
        requestSequence.current = sequence
        setLoading(true)
        setLoadError(false)
        setGuide(EMPTY_GUIDE)
        generateVibeDbPrompt(accountId ? { accountId } : {})
            .then(response => {
                if (requestSequence.current !== sequence) return
                setGuide(normalizeVibeCodexGuideResponse(response))
            })
            .catch(() => {
                if (requestSequence.current === sequence) setLoadError(true)
            })
            .finally(() => {
                if (requestSequence.current === sequence) setLoading(false)
            })
    }, [accountId])

    useEffect(() => {
        if (visible) {
            loadGuide()
        } else {
            requestSequence.current += 1
        }
    }, [visible, loadGuide])

    const copyPrompt = () => {
        if (!guide.prompt) return
        if (!navigator.clipboard) {
            message.warning('当前浏览器不支持自动复制，请手动选择复制')
            return
        }
        navigator.clipboard.writeText(guide.prompt)
            .then(() => message.success('完整 Prompt 已复制'))
            .catch(() => message.warning('自动复制失败，请手动选择复制'))
    }

    const goToApiKey = () => {
        onClose()
        history.push('/portal/profile?tab=apikey')
    }

    const sceneMessage = getVibeCodexGuideSceneMessage(scene)
    const titleSuffix = guide.promptVersion ? ` · ${guide.promptVersion}` : ''

    return (
        <Modal
            title={`用 Codex 查询 Vibe 数据${titleSuffix}`}
            visible={visible}
            width={920}
            onCancel={onClose}
            footer={[
                <Button key="apikey" icon={<KeyOutlined />} onClick={goToApiKey}>
                    去配置 API Key
                </Button>,
                <Button key="close" onClick={onClose}>关闭</Button>,
                <Button
                    key="copy"
                    type="primary"
                    icon={<CopyOutlined />}
                    disabled={!guide.prompt}
                    loading={loading}
                    onClick={copyPrompt}
                >
                    复制完整 Prompt
                </Button>,
            ]}
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="申请或授权项目，是为了让你的 Codex 能在授权范围内直接查询这些项目的数据。"
                description="Codex 会读取项目数据字典，把自然语言转换成只读 SQL，再使用接口返回的临时连接信息直连数据库查询；不会把 API Key 或数据库密码写入项目文件。"
            />
            {sceneMessage ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={sceneMessage}
                />
            ) : null}
            <Steps direction="vertical" size="small" current={-1} style={{ marginBottom: 8 }}>
                <Steps.Step
                    title="在 Codex 创建或选择一个专用项目目录"
                    description="建议把项目命名为「乐纯 Vibe 数据分析」，以后都在这个项目里新建数据查询任务。"
                />
                <Steps.Step
                    title="配置 LECHUN_API_KEY"
                    description="在个人中心 API Key 页复制当前系统对应的配置命令；配置后完全退出并重新打开 Codex，让环境变量生效。"
                />
                <Steps.Step
                    title="复制下方完整 Prompt 到 Codex 执行"
                    description="Prompt 会安全初始化项目并强制刷新当前账号已授权的全部 Vibe 项目字典，完成后即可用自然语言提问。"
                />
            </Steps>

            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="指南加载失败"
                    description={(
                        <Button size="small" icon={<ReloadOutlined />} onClick={loadGuide}>
                            重新加载
                        </Button>
                    )}
                />
            ) : null}
            {guide.missingProjectNames.length ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`以下项目尚无数据字典：${guide.missingProjectNames.join('、')}`}
                    description="这些项目暂时无法可靠生成 SQL，请联系项目所有者补充并同步 DATA_DICTIONARY.md。"
                />
            ) : null}

            <Space style={{ marginBottom: 8 }}>
                <Text strong>完整 Prompt</Text>
                {!loading && !loadError ? (
                    <Text type="secondary">
                        当前可查询 {guide.authorizedProjectCount} 个项目
                    </Text>
                ) : null}
            </Space>
            <Spin spinning={loading}>
                <Input.TextArea
                    value={guide.prompt}
                    rows={18}
                    readOnly
                    placeholder={loading ? '正在生成指南…' : '暂无可复制的 Prompt'}
                    style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 13, lineHeight: 1.6 }}
                />
            </Spin>
            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
                申请通过或以后新增授权时，请重新打开本指南并再次执行 Prompt，以刷新 Codex 的项目范围。
            </Paragraph>
        </Modal>
    )
}

export default withRouter(VibeCodexGuideDialog)
